import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import aduTaxesImage from '@/assets/adu-property-taxes.jpg';

const DoesADUIncreasePropertyTaxesCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/does-adu-increase-property-taxes-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={aduTaxesImage} 
              alt="ADU property tax implications" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              Does an ADU Increase Property Taxes in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Learn if and how adding an ADU impacts your property taxes in California. 
              Clarify the reassessment rules and potential tax implications.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Understanding California Property Tax Assessments
              </h2>
              <p className="text-white leading-relaxed mb-6">
                In California, property taxes are based on assessed value. When you add an ADU, 
                only the new construction is assessed at current market value. Your main home 
                retains its existing assessed value under Proposition 13 protections, meaning 
                the ADU addition won't trigger a reassessment of your entire property.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                When and How an ADU Can Increase Your Property Taxes
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Your property taxes will increase based on the assessed value of the ADU construction. 
                The county assessor determines this value based on construction costs, square footage, 
                and comparable sales. Typically, expect an annual tax increase of 1-1.5% of the 
                ADU's assessed construction value.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                California Prop 13 and ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Proposition 13 protects your main residence from reassessment when you add an ADU. 
                Only the new ADU construction is subject to current market assessment. This means 
                your existing home's low assessed value remains protected, and you only pay 
                additional taxes on the ADU portion.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: Will adding an ADU trigger a reassessment of my entire property?</p>
                  <p>A: No, only the ADU construction is reassessed, not the main residence.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Are ADUs subject to California's Proposition 13 tax protections?</p>
                  <p>A: Yes, the main home retains Prop 13 protections; only ADU additions are newly assessed.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DoesADUIncreasePropertyTaxesCalifornia;